import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { SectionTitle, EmptyState, Avatar, StatusPill, ShowMore } from "../../components/Atoms";
import { AIAuditButton, useAIAudit } from "../../components/AIAuditPanel";
import { Users, ArrowRight, Funnel, MagnifyingGlass, CaretDown, CaretUp } from "@phosphor-icons/react";

const AGE_BUCKETS = [
  { key: "all", label: "Any age", min: 0, max: 999 },
  { key: "18-23", label: "18-23", min: 18, max: 23 },
  { key: "24-29", label: "24-29", min: 24, max: 29 },
  { key: "30-34", label: "30-34", min: 30, max: 34 },
  { key: "34-39", label: "34-39", min: 35, max: 39 },
  { key: "40+",   label: "40+",   min: 40, max: 999 },
];

export default function ClientAmbassadorsPage() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [apps, setApps] = useState([]);
  const [filters, setFilters] = useState({
    project: "all", task: "all",
    name: "", course: "", college: "", skills: "", profession: "", instagram: "",
    gender: "", age: "all",
  });
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | score
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(20);
  const { openAudit, AuditModal } = useAIAudit();

  useEffect(() => { (async () => {
    const [a, p, s, ap] = await Promise.all([
      api.get("/applications?status=Approved"),
      api.get("/projects"),
      api.get("/submissions"),
      api.get("/ambassadors"),
    ]);
    setApps(a.data); setProjects(p.data); setSubmissions(s.data); setList(ap.data);
  })(); }, []);

  useEffect(() => {
    if (filters.project === "all") { setTasks([]); setFilters(f => ({ ...f, task: "all" })); return; }
    (async () => {
      const { data } = await api.get(`/tasks?project_id=${filters.project}`);
      setTasks(data);
      setFilters(f => ({ ...f, task: "all" }));
    })();
  }, [filters.project]);

  const approvedIds = useMemo(() => new Set(apps.map(a => a.ambassador_id)), [apps]);

  // Earliest application date per ambassador (association start date)
  const associationDate = useMemo(() => {
    const m = {};
    for (const a of apps) {
      const t = a.applied_at || a.created_at || "";
      if (!t) continue;
      if (!m[a.ambassador_id] || t < m[a.ambassador_id]) m[a.ambassador_id] = t;
    }
    return m;
  }, [apps]);

  const monthsSince = (iso) => {
    if (!iso) return null;
    const now = Date.now();
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    return Math.max(0, Math.floor((now - then) / (1000 * 60 * 60 * 24 * 30.44)));
  };
  const relLabel = (m) => (m == null ? "—" : m === 0 ? "This month" : m < 12 ? `${m} month${m === 1 ? "" : "s"}` : `${Math.floor(m / 12)} yr${m >= 24 ? "s" : ""}${m % 12 ? ` ${m % 12}m` : ""}`);

  const ageBucket = useMemo(() => AGE_BUCKETS.find(b => b.key === filters.age) || AGE_BUCKETS[0], [filters.age]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const nm = filters.name.trim().toLowerCase();
    const co = filters.course.trim().toLowerCase();
    const cl = filters.college.trim().toLowerCase();
    const sk = filters.skills.trim().toLowerCase();
    const pr = filters.profession.trim().toLowerCase();
    const ig = filters.instagram.trim().toLowerCase().replace("@", "");
    const gender = filters.gender;
    const result = list.filter(a => {
      if (!approvedIds.has(a.id)) return false;
      if (filters.project !== "all") {
        const inProj = apps.some(x => x.ambassador_id === a.id && x.project_id === filters.project);
        if (!inProj) return false;
        if (filters.task !== "all") {
          const onTask = submissions.some(s => s.ambassador_id === a.id && s.task_id === filters.task);
          if (!onTask) return false;
        }
      }
      if (q) {
        const hay = `${a.name || ""} ${a.city || ""} ${a.college || ""} ${a.instagram || ""} ${a.course || ""} ${a.profession || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (nm && !(a.name || "").toLowerCase().includes(nm)) return false;
      if (co && !(a.course || "").toLowerCase().includes(co)) return false;
      if (cl && !(a.college || "").toLowerCase().includes(cl)) return false;
      if (pr && !(a.profession || "").toLowerCase().includes(pr)) return false;
      if (ig && !((a.instagram || "").toLowerCase().replace("@", "")).includes(ig)) return false;
      if (gender && (a.gender || "") !== gender) return false;
      if (sk) {
        const ss = (a.skills || []).map(s => s.toLowerCase());
        if (!ss.some(s => s.includes(sk))) return false;
      }
      const age = Number(a.age || 0);
      if (filters.age !== "all" && !(age >= ageBucket.min && age <= ageBucket.max)) return false;
      return true;
    });
    // Sort
    const withDate = result.map(a => ({ a, d: associationDate[a.id] || "" }));
    if (sortBy === "newest") withDate.sort((x, y) => (y.d || "").localeCompare(x.d || ""));
    else if (sortBy === "oldest") withDate.sort((x, y) => (x.d || "").localeCompare(y.d || ""));
    else withDate.sort((x, y) => (y.a.performance_score || 0) - (x.a.performance_score || 0));
    return withDate.map(x => x.a);
  }, [list, approvedIds, apps, submissions, filters, search, ageBucket, sortBy, associationDate]);

  const resetFilters = () => setFilters({
    project: "all", task: "all", name: "", course: "", college: "", skills: "", profession: "",
    instagram: "", gender: "", age: "all",
  });

  return (
    <div className="space-y-5">
      <SectionTitle title="Your Creators" subtitle="Ambassadors approved across your campaigns" />

      <div className="glass p-3 space-y-3" data-testid="creator-filters">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1.5 text-xs uppercase font-bold text-muted tracking-wider"><Funnel size={14} weight="bold" /> Filter</div>
          <select value={filters.project} onChange={e => setFilters({...filters, project:e.target.value})} className="input-base text-sm py-1.5 max-w-[220px]" data-testid="creators-filter-project">
            <option value="all">All projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <select value={filters.task} onChange={e => setFilters({...filters, task:e.target.value})} disabled={filters.project === "all"} className="input-base text-sm py-1.5 max-w-[220px] disabled:opacity-40" data-testid="creators-filter-task">
            <option value="all">All tasks</option>
            {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              data-testid="creators-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, city, college, IG, course, profession"
              className="input-base text-sm py-1.5 pl-8 w-full"
            />
          </div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="input-base text-sm py-1.5" data-testid="creators-sort">
            <option value="newest">Newest linked</option>
            <option value="oldest">Oldest linked</option>
            <option value="score">Top performance</option>
          </select>
          <button onClick={() => setShowAdvanced(!showAdvanced)} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="toggle-advanced-filters">
            {showAdvanced ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />} Advanced
          </button>
          <div className="ml-auto text-xs text-muted whitespace-nowrap">Showing {filtered.length} of {list.filter(a => approvedIds.has(a.id)).length}</div>
        </div>
        {showAdvanced && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pt-1 border-t border-white/5" data-testid="advanced-filters">
            <input placeholder="Name contains…" value={filters.name} onChange={e => setFilters({...filters, name:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-name" />
            <input placeholder="Course" value={filters.course} onChange={e => setFilters({...filters, course:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-course" />
            <input placeholder="College" value={filters.college} onChange={e => setFilters({...filters, college:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-college" />
            <input placeholder="Skills contains…" value={filters.skills} onChange={e => setFilters({...filters, skills:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-skills" />
            <input placeholder="Profession" value={filters.profession} onChange={e => setFilters({...filters, profession:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-profession" />
            <input placeholder="Instagram" value={filters.instagram} onChange={e => setFilters({...filters, instagram:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-instagram" />
            <select value={filters.gender} onChange={e => setFilters({...filters, gender:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-gender">
              <option value="">Any gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            <select value={filters.age} onChange={e => setFilters({...filters, age:e.target.value})} className="input-base text-sm py-1.5" data-testid="filter-age">
              {AGE_BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
            <button onClick={resetFilters} className="btn-ghost text-xs col-span-2 sm:col-span-1" data-testid="filter-reset">Reset filters</button>
          </div>
        )}
      </div>

      {filtered.length === 0 ? <EmptyState icon={Users} title="No creators match your filters" /> : (
        <>
        <div className="glass p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="creators-table">
            <thead className="bg-white/5 text-[10px] uppercase font-bold text-muted tracking-wider">
              <tr>
                <th className="text-left p-2.5">Creator</th>
                <th className="text-left p-2.5 hidden md:table-cell whitespace-nowrap">City</th>
                <th className="text-left p-2.5 hidden lg:table-cell whitespace-nowrap">College</th>
                <th className="text-left p-2.5 hidden md:table-cell whitespace-nowrap">Instagram</th>
                <th className="text-right p-2.5 whitespace-nowrap">Followers</th>
                <th className="text-right p-2.5 whitespace-nowrap">Score</th>
                <th className="text-left p-2.5 hidden lg:table-cell whitespace-nowrap">With brand</th>
                <th className="text-left p-2.5 whitespace-nowrap">Status</th>
                <th className="text-right p-2.5 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, visible).map(a => {
                const projParam = filters.project !== "all" ? filters.project : null;
                const profileHref = projParam ? `/client/ambassadors/${a.id}?project=${projParam}` : `/client/ambassadors/${a.id}`;
                const months = monthsSince(associationDate[a.id]);
                return (
                  <tr key={a.id} className="border-t border-white/5 hover:bg-white/5 align-middle" data-testid={`client-amb-row-${a.id}`}>
                    <td className="p-2.5">
                      <Link to={profileHref} className="flex items-center gap-3 min-w-[180px]">
                        <Avatar src={a.photo} name={a.name} size={32} />
                        <span className="text-white font-semibold hover:text-[#22D3EE] truncate">{a.name}</span>
                      </Link>
                    </td>
                    <td className="p-2.5 hidden md:table-cell text-muted whitespace-nowrap">{a.city || "—"}</td>
                    <td className="p-2.5 hidden lg:table-cell text-muted truncate max-w-[180px]">{a.college || "—"}</td>
                    <td className="p-2.5 hidden md:table-cell text-[#EC4899] whitespace-nowrap">{a.instagram || "—"}</td>
                    <td className="p-2.5 text-right text-white whitespace-nowrap">{(a.instagram_followers || 0).toLocaleString()}</td>
                    <td className="p-2.5 text-right text-white whitespace-nowrap">{a.performance_score || 0}</td>
                    <td className="p-2.5 hidden lg:table-cell text-muted whitespace-nowrap" data-testid={`assoc-${a.id}`}>{relLabel(months)}</td>
                    <td className="p-2.5 whitespace-nowrap"><StatusPill status={a.status} /></td>
                    <td className="p-2.5 text-right">
                      <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                        <AIAuditButton
                          ambassadorId={a.id}
                          projectId={projParam}
                          onClick={() => openAudit(a.id, projParam)}
                          label={projParam ? "AI Audit (project)" : "AI Audit"}
                          testIdSuffix={`client-list-${a.id}`}
                        />
                        <Link to={profileHref} className="btn-ghost text-xs flex items-center gap-1 text-[#22D3EE]" data-testid={`view-profile-${a.id}`}>
                          Profile <ArrowRight size={12} weight="bold" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <ShowMore visible={visible} total={filtered.length} onMore={setVisible} step={20} testid="creators-show-more" />
        </>
      )}
      {AuditModal}
    </div>
  );
}
